import axios from '../../../../config/axios'
import AttachmentApiService from '../../../../services/AttachmentApiService'
import {
  ChecklistComment,
  ChecklistItem,
  ChecklistItemStatus,
  FileInChecklistItem,
  GeneralFile,
  ProductApprovalDetail,
  ProductApprovalListItem,
  ProductApprovalStatus,
} from './types'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
export const APPROVAL_API = `${GATEWAY_URL}/backend-fashion-orders/product-approval`

export interface CreateApprovalPayload {
  productId: number
  name: string
  type?: 'PILOT' | 'SHOWROOM'
  supplierCompanyId?: number | null
  approverId?: number | null
  startDate?: string | null
  expectedDeliveryDate?: string | null
  sizes?: string[]
}

export interface UpdateApprovalPayload {
  name?: string
  status?: ProductApprovalStatus
  supplierCompanyId?: number | null
  approverId?: number | null
  startDate?: string | null
  expectedDeliveryDate?: string | null
  sizes?: string[]
  frontImageId?: number | null
  sideImageId?: number | null
  backImageId?: number | null
}

export const ApprovalApi = {
  createShowroom: async (parentId: number, payload: { productCode: string; name: string }) => {
    const { data } = await axios.post(
      `${GATEWAY_URL}/backend-fashion-orders/fashion-product/${parentId}/showroom`,
      payload,
    )
    return data
  },

  listShowrooms: async (parentId: number) => {
    const { data } = await axios.get<any[]>(
      `${GATEWAY_URL}/backend-fashion-orders/fashion-product/${parentId}/showrooms`,
    )
    return data
  },

  deleteProduct: async (productId: number) => {
    await axios.delete(
      `${GATEWAY_URL}/backend-fashion-orders/fashion-product/${productId}`,
    )
  },

  duplicateShowroom: async (
    parentId: number,
    showroomId: number,
    payload: { productCode: string; name: string },
  ) => {
    const { data } = await axios.post(
      `${GATEWAY_URL}/backend-fashion-orders/fashion-product/${parentId}/showroom/${showroomId}/duplicate`,
      payload,
    )
    return data
  },


  list: async (productId: number) => {
    const { data } = await axios.get<ProductApprovalListItem[]>(APPROVAL_API, {
      params: { productId },
    })
    return data
  },

  getById: async (id: number) => {
    const { data } = await axios.get<ProductApprovalDetail>(`${APPROVAL_API}/${id}`)

    const signImage = async (img?: GeneralFile | null): Promise<GeneralFile | null> => {
      if (!img?.fileUrl) return img || null
      if (
        img.fileUrl.includes('X-Goog-Signature') ||
        img.fileUrl.includes('X-Amz-Signature') ||
        img.fileUrl.startsWith('blob:') ||
        img.fileUrl.startsWith('data:')
      ) {
        return img
      }
      try {
        const signed = await AttachmentApiService.getSignedUrl(img.fileUrl)
        if (signed) {
          return { ...img, fileUrl: signed, signedUrl: signed }
        }
      } catch (error) {
        console.error(error)
      }
      return img
    }

    const [FrontImage, SideImage, BackImage, ChecklistItems] = await Promise.all([
      signImage(data.FrontImage),
      signImage(data.SideImage),
      signImage(data.BackImage),
      Promise.all(
        (data.ChecklistItems || []).map(async (item) => {
          if (!item.Files || item.Files.length === 0) return item
          const signedFiles = await Promise.all(
            item.Files.map(async (f) => {
              if (!f.GeneralFile) return f
              const signedGf = await signImage(f.GeneralFile)
              return { ...f, GeneralFile: signedGf || f.GeneralFile }
            })
          )
          return { ...item, Files: signedFiles }
        })
      ),
    ])

    return {
      ...data,
      FrontImage,
      SideImage,
      BackImage,
      ChecklistItems,
    }
  },

  create: async (payload: CreateApprovalPayload) => {
    const { data } = await axios.post<ProductApprovalListItem>(APPROVAL_API, payload)
    return data
  },

  update: async (id: number, payload: UpdateApprovalPayload) => {
    const { data } = await axios.patch<ProductApprovalListItem>(
      `${APPROVAL_API}/${id}`,
      payload,
    )
    return data
  },

  remove: async (id: number) => {
    await axios.delete(`${APPROVAL_API}/${id}`)
  },

  duplicate: async (id: number) => {
    const { data } = await axios.post<ProductApprovalListItem>(
      `${APPROVAL_API}/${id}/duplicate`,
    )
    return data
  },

  promote: async (id: number) => {
    const { data } = await axios.post<ProductApprovalListItem>(
      `${APPROVAL_API}/${id}/promote`,
    )
    return data
  },

  createCard: async (
    id: number,
    payload: { targetPipelineId: number; targetStepId: number },
  ) => {
    const { data } = await axios.post(
      `${APPROVAL_API}/${id}/create-card`,
      payload,
    )
    return data
  },

  uploadFile: async (file: File): Promise<GeneralFile> => {
    const uploaded = await AttachmentApiService.upload(file)
    let fileUrl = uploaded.signedUrl || uploaded.fileUrl
    if (
      !fileUrl.includes('X-Goog-Signature') &&
      !fileUrl.includes('X-Amz-Signature') &&
      !fileUrl.startsWith('blob:') &&
      !fileUrl.startsWith('data:')
    ) {
      try {
        const signed = await AttachmentApiService.getSignedUrl(uploaded.fileUrl)
        if (signed) fileUrl = signed
      } catch (error) {
        console.error(error)
      }
    }
    return {
      id: uploaded.id,
      fileUrl,
      title: uploaded.title,
      fileName: uploaded.fileName,
      mimeType: uploaded.mimeType,
      originalName: uploaded.originalName,
    }
  },

  addChecklistItem: async (
    approvalId: number,
    payload: { description: string; approverId?: number | null; position?: number },
  ) => {
    const { data } = await axios.post<ChecklistItem>(
      `${APPROVAL_API}/${approvalId}/checklist`,
      payload,
    )
    return data
  },

  updateChecklistItem: async (
    itemId: number,
    payload: { description?: string; approverId?: number | null; position?: number },
  ) => {
    const { data } = await axios.patch<ChecklistItem>(
      `${APPROVAL_API}/checklist/${itemId}`,
      payload,
    )
    return data
  },

  removeChecklistItem: async (itemId: number) => {
    await axios.delete(`${APPROVAL_API}/checklist/${itemId}`)
  },

  decideChecklistItem: async (itemId: number, status: ChecklistItemStatus) => {
    const { data } = await axios.post<ChecklistItem>(
      `${APPROVAL_API}/checklist/${itemId}/decision`,
      { status },
    )
    return data
  },

  addChecklistComment: async (itemId: number, comment: string) => {
    const { data } = await axios.post<ChecklistComment>(
      `${APPROVAL_API}/checklist/${itemId}/comment`,
      { comment },
    )
    return data
  },

  updateChecklistComment: async (commentId: number, comment: string) => {
    const { data } = await axios.patch<ChecklistComment>(
      `${APPROVAL_API}/checklist/comment/${commentId}`,
      { comment },
    )
    return data
  },

  removeChecklistComment: async (commentId: number) => {
    await axios.delete(`${APPROVAL_API}/checklist/comment/${commentId}`)
  },

  addChecklistFile: async (itemId: number, file: File): Promise<FileInChecklistItem> => {
    const uploaded = await AttachmentApiService.upload(file)
    const [created] = await ApprovalApi.attachChecklistFiles(itemId, [uploaded.id])
    return created
  },

  attachChecklistFiles: async (
    itemId: number,
    generalFileIds: number[],
  ): Promise<FileInChecklistItem[]> => {
    const { data } = await axios.post<FileInChecklistItem[]>(
      `${APPROVAL_API}/checklist/${itemId}/attach-files`,
      { generalFileIds },
    )
    return data
  },

  removeChecklistFile: async (fileInItemId: number) => {
    await axios.delete(`${APPROVAL_API}/checklist/file/${fileInItemId}`)
  },
}
